/**
 * Figure S4b's "Overlap mini-map": the two inventions of the open group, with
 * the amber shared ground where their footprints meet and the strongest-pair
 * score sitting on it.
 *
 * Why not components/whitespace/OverlapMiniMap: that one draws outlines only —
 * no shaded intersection and no score — so it cannot carry the caption the
 * figure prints under it ("Amber is the shared ground"). Same projector
 * helpers, so this map and the Atlas can never disagree about where a shape is.
 *
 * Only the caller's OWN inventions are ever drawn, and only ones with a real
 * footprint. Two footprints are needed to shade between; with fewer the map
 * says so instead of shading nothing and looking broken.
 */
import {
  computeGroupFootprints,
  computeFootprintBounds,
  makeAtlasProjector,
  projectTagColor,
} from '../../lib/whitespace'
import {
  captionWidthFor,
  placeCaptions,
  scoreChipPosition,
  shortName,
  starPoints,
  type Disc,
  type Point,
} from './atlasGeometry'
import type { PortfolioMapLayout } from '../../types/whitespace'

export interface OwnOverlapMiniMapProps {
  layout: PortfolioMapLayout | null
  memberProjectIds: string[]
  projectNames: Map<string, string>
  /** The group's strongest-pair score, shown on the shared ground. */
  score?: number
}

const VIEWPORT = { width: 240, height: 200, padding: 24 }

export default function OwnOverlapMiniMap({
  layout,
  memberProjectIds,
  projectNames,
  score,
}: OwnOverlapMiniMapProps) {
  if (!layout) {
    return (
      <p data-testid="own-minimap-no-layout" className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11px] text-gray-500">
        No atlas layout yet — the mini-map draws the same footprints the Atlas does, so it needs
        that projection to have run at least once.
      </p>
    )
  }

  const ellipses = computeGroupFootprints(layout, memberProjectIds)
  if (ellipses.length === 0) {
    return (
      <p data-testid="own-minimap-empty" className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-[11px] text-gray-500">
        Not enough plotted documents to draw a footprint for either invention in this group, so
        there is nothing to shade between.
      </p>
    )
  }

  const bounds = computeFootprintBounds(ellipses)
  const project = makeAtlasProjector(bounds, VIEWPORT)
  const drawn = ellipses.map((e) => {
    const [cx, cy] = project([e.cx, e.cy])
    const [ex, ey] = project([e.cx + e.rx, e.cy + e.ry])
    return {
      project_id: e.project_id,
      cx,
      cy,
      rx: Math.abs(ex - cx),
      ry: Math.abs(ey - cy),
      colour: projectTagColor(e.project_id, memberProjectIds),
    }
  })
  const [first, second] = drawn
  const stars: Disc[] = drawn.map((e) => ({ cx: e.cx, cy: e.cy, r: 8 }))
  const captions = drawn.map((e) => ({
    key: e.project_id,
    label: shortName(projectNames.get(e.project_id) ?? e.project_id),
  }))
  // Each caption sits under its OWN shape; two that would read as one phrase go
  // on different lines instead (review round 3, item 8).
  const captionPos = placeCaptions(
    captions.map((c, i) => ({ key: c.key, x: drawn[i].cx, width: captionWidthFor(c.label) })),
    VIEWPORT,
  )
  const chip: Point | null =
    second && typeof score === 'number'
      ? scoreChipPosition([first.cx, first.cy], [second.cx, second.cy], stars, { width: 46, height: 17 }, VIEWPORT)
      : null

  return (
    <div data-part="OwnOverlapMiniMap" className="rounded-lg border border-gray-200 bg-white p-1.5">
      <svg
        width="100%"
        viewBox={`0 0 ${VIEWPORT.width} ${VIEWPORT.height}`}
        className="block rounded bg-[#F8FBFD]"
        role="img"
        aria-label="Shared ground between this group's inventions"
      >
        <defs>
          <clipPath id="own-minimap-clip">
            <ellipse cx={first.cx} cy={first.cy} rx={first.rx} ry={first.ry} />
          </clipPath>
        </defs>
        {drawn.map((e) => (
          <ellipse
            key={e.project_id}
            cx={e.cx}
            cy={e.cy}
            rx={e.rx}
            ry={e.ry}
            fill={e.colour}
            fillOpacity={0.14}
            stroke={e.colour}
            strokeWidth={1.4}
          />
        ))}
        {second && (
          <ellipse
            data-testid="own-minimap-shared-ground"
            cx={second.cx}
            cy={second.cy}
            rx={second.rx}
            ry={second.ry}
            fill="#FBBF24"
            fillOpacity={0.6}
            stroke="#B45309"
            strokeWidth={1}
            clipPath="url(#own-minimap-clip)"
          />
        )}
        {/* The figure draws the invention centre inside each footprint here too
            — the same ★ the atlas legend names (review round 1, item 7). */}
        {drawn.map((e) => (
          <polygon
            key={`star-${e.project_id}`}
            data-centre-star
            points={starPoints(e.cx, e.cy, 8, 3.4)}
            fill={e.colour}
            stroke="#ffffff"
            strokeWidth={1.2}
          >
            <title>{projectNames.get(e.project_id) ?? e.project_id}</title>
          </polygon>
        ))}
        {chip && (
          <g data-testid="own-minimap-score-chip">
            <rect
              x={chip[0] - 23}
              y={chip[1] - 8.5}
              width={46}
              height={17}
              rx={8.5}
              fill="#FBBF24"
              stroke="#B45309"
            />
            <text
              x={chip[0]}
              y={chip[1] + 3.5}
              textAnchor="middle"
              fontSize={10}
              fontWeight={800}
              fill="#111827"
            >
              {score}
            </text>
          </g>
        )}
        {/* The figure labels each shape under the map itself rather than in a
            key beside it. */}
        {captions.map((c) => (
          <text
            key={`name-${c.key}`}
            data-minimap-label={c.key}
            data-caption-width={captionWidthFor(c.label)}
            data-caption-line={captionPos.get(c.key)?.line ?? 0}
            x={captionPos.get(c.key)?.x ?? 0}
            y={VIEWPORT.height - 8 - (captionPos.get(c.key)?.line ?? 0) * 12}
            textAnchor="middle"
            fontSize={9.5}
            fontWeight={600}
            fill="#0C4A6E"
            paintOrder="stroke"
            stroke="#F8FBFD"
            strokeWidth={2.5}
            strokeLinejoin="round"
          >
            {c.label}
          </text>
        ))}
      </svg>

    </div>
  )
}
